import { makeObservable, observable, action, computed } from "mobx";
import localStorage from "mobx-localstorage";
import { track } from "@vercel/analytics";
import is from "is";
import { PublicClientApplication } from "@azure/msal-browser";
import LZString from "lz-string";
import axios from "axios";
import { config } from "../Config";
import {
  getUrlParameterForDriveItemInfo,
  compressParameters,
  openInSameTab,
  openInNewTab,
  isNumber,
  tryParseInt,
  jsonCopy,
} from "../utils/helper";
import { getCellFormatFromExcelFormat } from "../utils/helperExcel";
import { getFileUrlFromDriveInfo, checkPublicAccessToken } from "./GraphMain";
import { createNewRange } from "./graphRangeFunctions";
import {
  getWorkbookFullInfo,
  getWorkbookFileInfo,
} from "./graphWorkbookFunctions";
import {
  getHistoryFromDocument,
  getHistoryForElement,
} from "./graphWorkbookHistoryFunctions";
import {
  writeGraphAppInfoValue,
  deleteGraphAppInfoValue,
  duplicateGraphAppInfoValue,
} from "./graphAppInfoFunctions";
import {
  getPermissionsForDriveItem,
  addPermissionsForDriveItem,
  addShareLinkForDriveItem,
  removePermissionsForDriveItem,
  getDriveItemInfoFromWebUrl,
  downloadDocumentItem,
} from "./graphOneDriveFunctions";

export class StoreWorkbooks {
  activeDriveItemId = "";
  activeFileNeedsRefresh = false;
  activeDriveItemInfo = undefined;
  activeFileUrl = "";
  activeSpread = null;
  documentInfos = [];
  chartInfos = observable([]);
  recentsList = observable([]);
  excelFiles = observable([]);
  isLoading = false;
  publicClientApplication = undefined; //PublicClientApplication;
  userHasFileAccess = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.recentsList = [];

    makeObservable(this, {
      isLoading: observable,
      setIsLoading: action,
      activeDriveItemId: observable,
      activeDriveItemInfo: observable,
      activeFileUrl: observable,
      // rights
      userHasFileAccess: observable,
      setUserHasFileAccess: action,
      // recents
      recentsList: observable,
      addRecentsDocument: action,
      getRecentsCount: computed,
    });
  }

  getClientApplication() {
    if (
      !this.publicClientApplication ||
      this.rootStore.microsoftAccessTokenType === "public"
    ) {
      // const isDesktopClient = window.hasOwnProperty("todesktop");
      const isDesktopSafari = this.rootStore.isSafari && !this.rootStore.isIOS;
      const navigateToLoginRequestUrl = false; //isDesktopSafari ? true : false;
      const redirectUri = process.env.REACT_APP_AUTH_REDIRECTURI;

      this.publicClientApplication = new PublicClientApplication({
        auth: {
          clientId: config.appId,
          // authority: "https://login.microsoftonline.com/common",
          redirectUri: redirectUri,
          navigateToLoginRequestUrl: navigateToLoginRequestUrl, // || isDesktopClient
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: isDesktopSafari ? false : true,
        },
      });
      // wait
      this.publicClientApplication.handleRedirectPromise();
    }

    return this.publicClientApplication;
  }

  //========================
  // #region SPREAD

  async setActiveSpread(spread) {
    this.activeSpread = spread;
  }

  // #endregion
  //========================

  //========================
  // #region FILES

  async setUserHasFileAccess(access) {
    console.log("INFO userHasFileAccess: ", access);
    this.userHasFileAccess = access;
  }

  async startConsentFlow(callback) {
    if (callback) {
      // e.g. hide the share dialog
      callback("share");
    }
    let redirect_uri =
      process.env.NODE_ENV === "production"
        ? "https://app.airrange.io/api/registerconnector"
        : "http://localhost:3000/api/registerconnector";

    let stateJson = {
      tenantId: this.rootStore.main.userTenantId, //  "3ad0dd0b-0012-482c-887e-292beac7e3a0",
      user: this.rootStore.main.userEmail,
      client: this.rootStore.isTeams ? "teams" : "web",
    };
    let state = compressParameters(stateJson);

    const appId = process.env.REACT_APP_AZURE_APP_ID;
    const GETCODE_CALL =
      "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=" +
      appId +
      "&response_type=code&redirect_uri=" +
      encodeURIComponent(redirect_uri) +
      "&response_mode=query&scope=offline_access%20files.readwrite.all&state=" +
      state;

    // write a flag to refresh the access token
    // this.rootStore.main.setLocalStoreValue("AFTER_FILEACCESS_CONSENT", true);
    // open the admin consent page
    if (this.rootStore.isTeams) {
      openInNewTab(GETCODE_CALL);
    } else {
      openInSameTab(GETCODE_CALL);
    }
  }

  // #endregion
  //========================

  //========================
  // #region PERMISSIONS

  async getPermissionsForDriveItem(accessInfo) {
    return await getPermissionsForDriveItem(accessInfo.driveItemInfo);
  }

  async addPermissionsForDriveItem(accessInfo, email, role, callback) {
    await addPermissionsForDriveItem(accessInfo.driveItemInfo, email, role);
    if (callback) {
      callback();
    }
    return;
  }

  async removePermissionsForDriveItem(accessInfo, permissionId, callback) {
    await removePermissionsForDriveItem(accessInfo.driveItemInfo, permissionId);
    if (callback) {
      callback();
    }
    return;
  }

  async addShareLinkForDriveItem(accessInfo, role, callback) {
    await addShareLinkForDriveItem(accessInfo.driveItemInfo, role);
    if (callback) {
      callback();
    }
    return;
  }

  // #endregion
  //========================

  //========================
  // #region RECENTS

  async getRecentDocuments() {
    // get the public apps
    // const publicList = getLastPublicAppsList();
    // load the recent documents from the local storage
    let recentsList =
      this.getLocalStoreValue("recentsList-" + this.rootStore.main.userEmail) ||
      [];
    // check for duplicates
    const uniqueList = [];
    for (const item of recentsList) {
      const isDuplicate = uniqueList.find(
        (obj) => obj.parametersUrl === item.parametersUrl
      );
      if (!isDuplicate) {
        uniqueList.push(item);
      }
    }

    // add the public apps
    // publicList.forEach((item) => {
    //   const foundIndex = uniqueList.findIndex(
    //     (value) => value.parametersUrl === item.parametersUrl
    //   );
    //   if (foundIndex === -1) uniqueList.push(item);
    // });

    this.recentsList = uniqueList;
  }

  async addRecentsDocument(doc) {
    if (!doc || !doc.id) return;
    try {
      const docName = doc.name?.split(".xls").shift();
      const tempDocument = {
        id: doc.id,
        // driveItemInfo: doc.driveItemInfo,
        type: doc.type,
        elementType: doc.elementType,
        name: doc.name,
        caption: docName,
        parametersUrl: doc.parametersUrl,
        designInfo: doc.designInfo,
        timeStamp: doc.timeStamp,
        isPublic: doc.isPublic,
        data: doc.data,
      };
      // dynamic values
      if (doc.driveItemInfo) tempDocument.driveItemInfo = doc.driveItemInfo;
      if (doc.fileType) tempDocument.fileType = doc.fileType;
      if (doc.fileId) tempDocument.fileId = doc.fileId;
      if (doc.topImageUrl) tempDocument.topImageUrl = doc.topImageUrl;
      // remove old same documents
      const tempList = this.recentsList.filter((x) => x.id !== doc.id);
      tempList.unshift(tempDocument);
      this.recentsList = [...tempList.slice(0, 100)];
      this.setLocalStoreValue(
        "recentsList-" + this.rootStore.main.userEmail,
        this.recentsList
      );
    } catch (error) {
      console.log("ERROR addRecentsDocument: ", error);
    }
  }

  async removeRecentsDocument(id) {
    if (!id) return;
    try {
      // remove old same documents
      const tempList = this.recentsList.filter((x) => x.id !== id);
      this.recentsList = [...tempList.slice(0, 100)];
      this.setLocalStoreValue(
        "recentsList-" + this.rootStore.main.userEmail,
        this.recentsList
      );
    } catch (error) {
      console.log("ERROR addRecentsDocument: ", error);
    }
  }

  async renameRecentsDocument(id, newName) {
    if (!id || !newName) return;
    try {
      // remove old same documents
      const item = this.recentsList.find((x) => x.id === id);
      if (!item) return;

      if (item.caption) item.caption = newName;
      if (item.name) item.name = newName;
      this.setLocalStoreValue(
        "recentsList-" + this.rootStore.main.userEmail,
        this.recentsList
      );
    } catch (error) {
      console.log("ERROR renameRecentsDocument: ", error);
    }
  }

  clearAllRecents() {
    this.recentsList = [];
    this.setLocalStoreValue(
      "recentsList-" + this.rootStore.main.userEmail,
      this.recentsList
    );
  }

  get getRecentsCount() {
    return this.recentsList.length;
  }

  // #endregion
  //========================

  //========================
  // #region DOCUMENTS

  getDocumentInfoForDriveItemId(driveItemId) {
    const tempInfo = this.documentInfos.find((x) => x.id === driveItemId);
    return tempInfo?.documentInfo;
  }

  getDocumentUrlForDriveItemId(driveItemId) {
    const tempInfo = this.documentInfos.find((x) => x.id === driveItemId);
    return tempInfo?.documentUrl;
  }

  isDocumentInfoForDriveItemIdAvailable(driveItemId) {
    const tempInfo = this.documentInfos.find((x) => x.id === driveItemId);
    return tempInfo !== undefined;
  }

  addDocumentInfo(documentInfo, documentParameters) {
    // look for existing entry
    if (this.getDocumentInfoForDriveItemId(documentInfo.fileInfo.id)) return;

    let documentUrl = window.location.hash;
    // get the right parameter string
    if (!documentUrl.includes("/workbook/")) {
      const driveItemInfo = documentInfo.accessInfo?.driveItemInfo;
      const compressedParameter = getUrlParameterForDriveItemInfo(
        driveItemInfo,
        this.rootStore.encryptionVersion
      );
      documentUrl = "/workbook/" + compressedParameter;
    }
    // add this new entry
    this.documentInfos.push({
      id: documentInfo.fileInfo.id,
      timestamp: new Date(),
      documentInfo: documentInfo,
      documentUrl: documentUrl,
      elements: [],
      changes: [],
    });
  }

  checkAccessInfo(accessInfo) {
    //const tempInfo = this.documentInfos.find((x) => x.id === accessInfo.driveItemId);
    return accessInfo;
  }

  openDriveItemWorkbook(driveItemInfo) {
    if (!driveItemInfo) return;
    // get the right parameter string
    const compressedParameter = getUrlParameterForDriveItemInfo(
      driveItemInfo,
      this.rootStore.encryptionVersion
    );
    this.rootStore.openUrl("/workbook/" + compressedParameter);
  }

  getAccessInfoForDriveItemInfo(driveItemInfo, publicTenant, connectorId) {
    if (!driveItemInfo) return;
    let tempInfo = this.documentInfos.find(
      (x) => x.id === driveItemInfo.driveItemId
    );

    let accessInfo = tempInfo
      ? tempInfo.documentInfo?.accessInfo
      : {
          driveItemInfo: driveItemInfo,
          tenantId: publicTenant,
          connectorId: connectorId,
        };

    return jsonCopy(accessInfo);
  }

  getDriveItemInfoForFileRes(fileRes) {
    if (!fileRes) return;

    let driveItemInfo = {
      driveItemId: fileRes.id,
      driveItemType: "",
      fileName: fileRes.name,
      fileUrl:
        "/drives/" + fileRes.parentReference?.driveId + "/items/" + fileRes.id,
      fileWebUrl: fileRes.webUrl,
      shareType: "owner",
      creatorEmail: fileRes.createdBy?.user?.email,
    };
    return driveItemInfo;
  }

  async getWorkbookDetail(accessInfo, sharingInfo) {
    const driveItemId = accessInfo.driveItemInfo.driveItemId;
    let documentInfo = undefined;
    // try to get it from the cache
    documentInfo = this.getDocumentInfoForDriveItemId(driveItemId);
    if (documentInfo && !this.activeFileNeedsRefresh) {
      console.log("INFO - used documentsInfo from cache");
    }
    if (this.activeFileNeedsRefresh) {
      documentInfo = null;
      this.documentInfos.length = 0;
      console.log("INFO - clear documentsInfo cache");
    }

    // check public document
    if (accessInfo.connectorId) {
      console.time("PROFILE checkPublicAccessToken");
      // create a session entry for this accessToken & driveItemInfo
      await checkPublicAccessToken(accessInfo, sharingInfo);
      console.timeEnd("PROFILE checkPublicAccessToken");
    }

    // get the documentInfo if no cache is available
    if (!documentInfo) {
      // get the whole document
      documentInfo = await getWorkbookFullInfo(
        accessInfo,
        this.chartDetailsReady
      );
      if (!documentInfo || documentInfo === "error") return null;

      // store workbook formulas and data locally - offline calculations
      // let workbookJson = await getWorkbookDataAsJson(accessInfo);
      // this.rootStore.main.setLocalStoreValue(
      //   driveItemId + "-jsondata",
      //   workbookJson
      // );

      // cache the object
      this.addDocumentInfo(documentInfo);
      // inform the store
      this.setActiveFile(documentInfo.accessInfo, "workbook");
      this.setActiveFileNeedsRefresh(false);
    }
    this.rootStore.main.setLoadingString(undefined);
    // track
    this.rootStore.main.withTracking &&
      track("workbook-detail-get", {
        name: driveItemId,
        type: "M365",
      });

    return documentInfo;
  }

  async getWorkbookDataAsJson(driveItemInfo, callback) {
    if (!driveItemInfo || !callback) return;

    const ExcelIO = await import("@mescius/spread-excelio");
    ExcelIO.LicenseKey = process.env.REACT_APP_SPREADJS18_KEY;

    // get the download url
    const accessInfo = this.getAccessInfoForDriveItemInfo(driveItemInfo);
    let fileInfo = await getWorkbookFileInfo(accessInfo);
    let downloadUrl = fileInfo?.downloadUrl;
    if (!downloadUrl) {
      callback(null);
      return;
    }

    // get the file from OneDrive
    let response = await axios({
      url: downloadUrl,
      method: "GET",
      responseType: "blob", // important
    });
    if (!response) {
      callback(null);
      return;
    }

    console.log("INFO workbook size", response.data?.size);
    let excelIO = new ExcelIO.IO();

    excelIO.open(
      response.data,
      (json) => {
        callback(json);
      },
      (e) => {
        callback(null);
        console.log("ERROR ExcelIo open", e);
      }
    );
  }

  async getSpreadJsonFromFile(file, GC, callback) {
    if (!file || !callback || !GC) return;

    //==============================================
    // usting spread import
    //==============================================
    const initSpread = new GC.Spread.Sheets.Workbook();

    // Using import method to load xlsx file
    initSpread.suspendPaint();
    initSpread.suspendCalcService();
    initSpread.import(
      file,
      () => {
        const spreadJson = initSpread.toJSON();
        callback(spreadJson);
      },
      (error) => {
        console.log("ERROR getSpreadsheetJsonFromFile", error);
        callback(null);
      }
    );
  }

  async getSpreadJsonFromFileAsync(file, GC) {
    if (!file || !GC) return;

    return new Promise((resolve, reject) => {
      const initSpread = new GC.Spread.Sheets.Workbook();
      // Using import method to load xlsx file
      initSpread.suspendPaint();
      initSpread.suspendCalcService();
      initSpread.import(
        file,
        (json) => {
          resolve(json);
        },
        (error) => {
          console.log("ERROR getSpreadsheetJsonFromFile", error);
          reject(error);
        }
      );
    });
  }

  getWorkbookCellProperties(sheet, row, col, options) {
    let properties = [];
    if (!sheet || (!row && row !== 0) || (!col && col !== 0)) return properties;
    let elementType;
    let hasFormula = false;
    let cellText = sheet.getText(row, col);
    properties.push({ propertyId: "value", value: cellText });
    let cellValue = sheet.getValue(row, col);
    properties.push({ propertyId: "originValue", value: cellValue });
    const comment = sheet?.comments.get(row, col)?.text();
    if (comment) {
      properties.push({ propertyId: "comment", value: comment });
    }
    let cellFormula = sheet.getFormula(row, col);
    if (cellFormula) {
      hasFormula = true;
      elementType = "ResultCell";
      properties.push({ propertyId: "isFormula", value: true });
    }
    // ============================
    // Auto-Title
    // ============================
    let leftCellText = "";
    if (col > 0) {
      let initCol = 1;
      while (initCol < 4) {
        const cellContent = sheet.getText(row, col - initCol) ?? "";
        const cellNextValue = sheet.getValue(row, col - initCol);
        const cellIsNotANumber = isNaN(cellNextValue);
        const cellIsNoFormula = sheet.getFormula(row, col - initCol) === null;
        if (cellContent.length > 0 && cellIsNotANumber && cellIsNoFormula) {
          leftCellText = sheet.getText(row, col - initCol);
          break;
        }
        initCol++;
      }
      if (leftCellText)
        properties.push({ propertyId: "auto_title", value: leftCellText });
    }
    if (row > 0 && !leftCellText) {
      let topCellText = "";
      let initRow = 1;
      while (initRow < 4) {
        const cellContent = sheet.getText(row - initRow, col) ?? "";
        const cellNextValue = sheet.getValue(row - initRow, col);
        const cellIsNotANumber = isNaN(cellNextValue);
        const cellIsNoFormula = sheet.getFormula(row - initRow, col) === null;
        if (cellContent.length > 0 && cellIsNotANumber && cellIsNoFormula) {
          topCellText = sheet.getText(row - initRow, col);
          break;
        }
        initRow++;
      }
      if (topCellText)
        properties.push({ propertyId: "auto_title", value: topCellText });
    }
    // ============================
    // Auto-Dropdown items
    // ============================
    let sheetName = sheet.name();
    let validation = sheet.getDataValidator(row, col);
    if (validation) {
      let validationJson = validation.toJSON();
      let condition = validationJson.condition;
      if (condition?.conType === 12) {
        let formula = condition?.formula?.replace(/\$/g, "");
        if (formula && typeof formula === "string") {
          if (!formula.includes("!")) formula = sheetName + "!" + formula;
        }
        if (condition?.expected) {
          const str = condition?.expected ?? "";
          const arr = str.split(",") ?? [];
          let finalArr = [];
          arr.forEach((item) => {
            if (is.string(item)) {
              const isItemANumber = isNumber(item);
              const newValue = isItemANumber ? tryParseInt(item) : item.trim();
              finalArr.push(newValue);
            } else {
              finalArr.push(item);
            }
          });
          properties.push(
            { propertyId: "dropdown_items", value: finalArr },
            { propertyId: "items_count", value: finalArr.length }
          );
        }
        if (formula) {
          const cellInfo = sheet.getRange(formula);
          const itemsCount = cellInfo?.colCount * cellInfo?.rowCount;
          properties.push({ propertyId: "items_count", value: itemsCount });
        }
        properties.push({ propertyId: "source_address", value: formula });
        if (!hasFormula) elementType = "Dropdown";
      }
    }
    // ============================
    // Number Formats
    // ============================
    let min;
    let max;
    let step;
    if (validation) {
      const validationJson = validation.toJSON();
      let minValue =
        validationJson?.condition?.item1?.conType === 1 &&
        validationJson?.condition?.item1?.formula;
      if (minValue && (!isNaN(minValue) || minValue === "0")) {
        min = parseInt(minValue);
      }
      let maxValue =
        validationJson?.condition?.item2?.conType === 1 &&
        validationJson?.condition?.item2?.formula;
      if (maxValue && (!isNaN(maxValue) || maxValue === "0")) {
        max = parseInt(maxValue);
      }
    }
    let cellStyle = sheet.getStyle(row, col);
    if (cellStyle) {
      let formatter = getCellFormatFromExcelFormat(cellStyle.formatter);
      if (formatter) {
        if (!hasFormula && !elementType) elementType = "NumberInput";
        properties.push({ propertyId: "number_format", value: formatter });
        if (formatter.includes("%")) {
          step = 0.01;
          min = 0;
          max = 1;
        }
      }
      // currency format
      let originalFormatter = cellStyle.formatter;
      let currencySign;
      if (originalFormatter?.includes("€")) {
        currencySign = "€";
      }
      if (originalFormatter?.includes("$")) {
        currencySign = "$";
      }
      if (originalFormatter?.includes("£")) {
        currencySign = "£";
      }
      if (currencySign) {
        properties.push({
          propertyId: "number_format_suffix",
          value: currencySign,
        });
      }
      if (
        !Number.isNaN(Number(cellValue)) &&
        Number.isInteger(Number(cellValue))
      ) {
        // Set step value according to cell number
        if ((min || min === 0) && (max || max === 0)) {
          const numberValue = Number(cellValue);
          let numberOrigin = 100;
          step = 1;
          while (numberValue > numberOrigin) {
            numberOrigin = numberOrigin * 10;
            step = step * 10;
          }
        }
      }
      let format =
        formatter?.toLowerCase().replace("[", "").replace("]", "") ?? "";
      // Date Picker
      if (!hasFormula && format.includes("yy")) {
        elementType = "DatePicker";
      }
      // Time Picker
      if (!hasFormula && format.includes("h:mm")) {
        elementType = "TimePicker";
      }
    }
    if (min || min === 0)
      properties.push({ propertyId: "min_value", value: min });
    if (max || max === 0)
      properties.push({ propertyId: "max_value", value: max });
    if ((min || min === 0) && (max || max === 0) && !step) step = 1;
    if (min === 0 && max === 1 && step === 1) step = 0.01;
    if (step) properties.push({ propertyId: "step", value: step });
    if (
      (min || min === 0) &&
      (max || max === 0) &&
      hasFormula === false &&
      (!elementType || elementType === "NumberInput")
    ) {
      elementType = "Range";
    }
    // Text Input
    if (!elementType && cellValue && isNaN(cellValue)) {
      elementType = "TextInput";
    }

    if (typeof cellValue === "boolean") {
      properties.push({
        propertyId: "boolean_status",
        value: cellValue,
      });
      elementType = "Checkbox";
    }

    if (!elementType) elementType = "NumberInput";
    properties.push({
      propertyId: "elementType",
      value: elementType,
    });
    // ============================
    // Colors
    // ============================
    if (cellStyle?.backColor) {
      let backColor = cellStyle?.backColor;
      let foreColor = cellStyle?.foreColor;
      if (backColor?.includes("#")) {
        properties.push({
          propertyId: "background_active",
          value: backColor,
        });
      }
      if (foreColor?.includes("#")) {
        properties.push({
          propertyId: "text_active",
          value: foreColor,
        });
      }
    }
    return properties;
  }

  async chartDetailsReady(accessInfo, chartsList) {}

  async getDocumentHistory(accessInfo, elements) {
    accessInfo = this.checkAccessInfo(accessInfo);
    let history = getHistoryFromDocument(accessInfo, elements);
    // track
    this.rootStore.main.withTracking &&
      track("workbook-history-get", {
        name: accessInfo.driveItemId,
        type: "M365",
      });

    return history;
  }

  async getElementHistory(accessInfo, elementId) {
    accessInfo = this.checkAccessInfo(accessInfo);
    let history = getHistoryForElement(accessInfo, elementId);
    // track
    this.rootStore.main.withTracking &&
      track("element-history-get", {
        name: accessInfo.driveItemId,
        type: "M365",
      });

    return history;
  }

  async writeAppInfoValue(accessInfo, infoId, infoValue, messageCallback) {
    if (!accessInfo || !infoId) return null;

    const res = await writeGraphAppInfoValue(
      accessInfo,
      infoId,
      infoValue,
      messageCallback
    );
    // clear the element cache
    this.rootStore.elements.clearElementCacheList();
    // track
    this.rootStore.main.withTracking &&
      track("workbook-appinfo-write", {
        name: accessInfo.driveItemId,
        type: "M365",
      });

    return res;
  }

  async deleteAppInfoValue(driveItemInfo, infoId, messageCallback) {
    if (!driveItemInfo || !infoId) return null;

    const res = await deleteGraphAppInfoValue(driveItemInfo, infoId);
    // clear the element cache
    this.rootStore.elements.clearElementCacheList();
    // track
    this.rootStore.main.withTracking &&
      track("workbook-appinfo-delete", {
        name: driveItemInfo.driveItemId,
        type: "M365",
      });

    return res;
  }

  async duplicateAppInfoValue(driveItemInfo, infoId, prefix, messageCallback) {
    if (!driveItemInfo || !infoId) return null;

    const res = await duplicateGraphAppInfoValue(driveItemInfo, infoId, prefix);
    this.setActiveFileNeedsRefresh(true);
    // track
    this.rootStore.main.withTracking &&
      track("workbook-appinfo-duplicate", {
        name: driveItemInfo.driveItemId,
        type: "M365",
      });

    return res;
  }

  async processApiParameters(parameters) {
    let urlParameters = parameters.replace("#/api?", "").replace("/api?", "");
    let fileUrl;
    let driveItemInfo;
    let result = LZString.decompressFromEncodedURIComponent(urlParameters);
    let object = JSON.parse(result);
    if (object) {
      fileUrl = object.fileUrl;
      driveItemInfo = await getDriveItemInfoFromWebUrl(fileUrl);
      // is there an element info
      let element = object.element;
      if (driveItemInfo && element) {
        let elementId = element.id;
        let elementType = element.type;
        let elementName = element.name;
        let worksheetId = element.worksheet?.id;
        let options = null;
        // jump to element
        this.rootStore.elements.openElementFromExternal(
          driveItemInfo,
          elementId,
          elementType,
          elementName,
          worksheetId,
          options
        );
        return;
      }
      // jump to workbook
      if (driveItemInfo) {
        this.openDriveItemWorkbook(driveItemInfo);
      } else {
        return "error";
      }
    }
  }

  // DOCUMENT RANGES

  async createDocumentNamedRange({
    accessInfo,
    nameId,
    nameCaption,
    nameRange,
  }) {
    let newNameList = await createNewRange(
      accessInfo,
      nameId,
      nameCaption,
      nameRange
    );
    // refresh the parent document the next time
    this.setActiveFileNeedsRefresh(true);
    return newNameList;
  }

  // #endregion
  //========================

  // FUNCTIONS

  setIsLoading(loading) {
    this.isLoading = loading;
  }

  setActiveFile(accessInfo, fileMode) {
    this.activeDriveItemId = accessInfo.driveItemInfo.driveItemId;
    this.activeDriveItemInfo = accessInfo.driveItemInfo;
    this.activeFileUrl = getFileUrlFromDriveInfo(accessInfo.driveItemInfo);
  }

  clearActiveFile() {
    this.activeDriveItemId = undefined;
    this.activeDriveItemInfo = undefined;
    this.activeFileUrl = undefined;
  }

  getLocalStoreValue(name, defaultValue) {
    let tempValue = localStorage.getItem(name);
    return tempValue ? tempValue : defaultValue;
  }

  setLocalStoreValue(name, actualValue) {
    localStorage.setItem(name, actualValue);
  }

  setActiveFileNeedsRefresh(value = true) {
    this.activeFileNeedsRefresh = value;
    // clear the element cache
    this.rootStore?.elements?.clearElementCacheList();
  }
}
